import { createAsyncThunk } from '@reduxjs/toolkit';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebaseConfig.js';
import { getDisplayName } from '../../../utils/userDisplay.js';
import { validateSolution } from '../../../utils/validateSolution.js';
import { tokenizeWords } from './utils.js';

export const createSolution = createAsyncThunk(
  'solutions/create',
  async (solutionData, { getState, extra }) => {
    const { auth: { currentUser }, solutions: { builder } } = getState();
    // Dynamically import getUserData to avoid circular deps
    const lowQuota = process.env.REACT_APP_LOW_QUOTA_MODE === 'true' || process.env.REACT_APP_DISABLE_SOLUTION_LISTENERS === 'true';
    const { getUserData } = lowQuota ? { getUserData: async () => null } : await import('../../../pages/auth/utils/firebase/userOperations.js');
    let userProfile = null;
    try {
      userProfile = currentUser?.uid ? await getUserData(currentUser.uid) : null;
    } catch (err) {
      console.warn('Could not fetch user profile for solution creation:', err);
    }
    const creatorUsername = getDisplayName({
      profile: userProfile || null,
      authUser: currentUser || null,
      candidates: [currentUser?.displayName]
    });
    const creatorProfession = userProfile?.userProfession || userProfile?.profession || '';
    // Use solutionMode from passed argument first to avoid race condition
    const solutionMode = solutionData?.solutionMode || builder.solution.solutionMode || 'detailed';
    const titleLower = (builder.solution.title || '').toLowerCase().trim();
    const typeLower = (builder.solution.type || '').toLowerCase().trim();
    const titleWordsLower = tokenizeWords(builder.solution.title || '');

    const solutionWithPhases = {
      ...solutionData,
      // Always use latest title/type from builder state, ignore any in solutionData
      title: builder.solution.title,
      type: builder.solution.type,
      postId: builder.solution.postId,
      solutionMode,
      // Include phases only for detailed solutions
      phases: solutionMode === 'detailed' ? builder.phases : [],
      creatorId: currentUser.uid,
      creatorUsername,
      creatorProfession,
      upvotes: 0,
      voters: [],
      likes: 0,
      likers: [],
      saves: 0,
      savers: [],
      shares: 0,
      sharers: [],
      // Derived fields for search
      title_lower: titleLower,
      type_lower: typeLower,
      title_words_lower: titleWordsLower,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Always include content field (solution summary) for all solutions
    solutionWithPhases.content = builder.solution.content || '';

    // Validate the solution before saving
    const validation = validateSolution(solutionWithPhases);

    // If validation fails, throw an error with the validation messages
    if (!validation.isValid) {
      throw new Error(`Solution validation failed: ${validation.errors.join('; ')}`);
    }
    try {
      const docRef = await addDoc(collection(db, 'solutions'), solutionWithPhases);
      // In low quota mode, avoid an immediate read of the created doc
      if (lowQuota) {
        return { id: docRef.id, ...solutionWithPhases };
      }
      return { id: docRef.id, ...solutionWithPhases };
    } catch (err) {
      console.error('Firestore addDoc failed:', err);
      throw err;
    }
  }
);
